import { useState, useEffect } from 'react';
import { renderCampaignPreview, type CampaignFallback } from '../../api/admin';

interface CampaignEmailPreviewProps {
  campaignId: string;
  /** Subject shown in the header until the faithful render loads. */
  initialSubject?: string;
}

/**
 * SDA-192: queue/editor email preview. Fetches the campaign rendered EXACTLY as
 * a recipient receives it (variables resolved with friendly fallbacks +
 * unsubscribe footer) via /render-preview — render-only, never sends. This is
 * the single source of truth with the real send + the "Send test to myself".
 */
export default function CampaignEmailPreview({ campaignId, initialSubject }: CampaignEmailPreviewProps) {
  const [loading, setLoading] = useState(true);
  const [html, setHtml] = useState('');
  const [subject, setSubject] = useState(initialSubject ?? '');
  const [fallbacks, setFallbacks] = useState<CampaignFallback[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    renderCampaignPreview(campaignId)
      .then((res) => {
        if (cancelled) return;
        setHtml(res.html);
        setSubject(res.subject || initialSubject || '(no subject)');
        setFallbacks(res.fallbacks_applied ?? []);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : 'Failed to load preview');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [campaignId, initialSubject]);

  return (
    <div className="rounded-xl border border-warm-200 overflow-hidden shadow-sm flex flex-col bg-white">
      {/* Dark header */}
      <div className="bg-warm-800 px-4 py-2.5 flex items-center justify-between gap-3 flex-shrink-0">
        <div className="min-w-0 flex-1">
          <p className="text-[10px] text-warm-400 uppercase tracking-widest font-semibold mb-0.5">Email Preview</p>
          <p className="text-xs text-warm-200 truncate font-medium">{subject || '(no subject)'}</p>
        </div>
        <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold whitespace-nowrap flex-shrink-0 bg-success-700 text-success-100">
          ✓ As recipients see it
        </span>
      </div>

      {/* Body */}
      {loading ? (
        <div className="flex-1 flex items-center justify-center py-16 bg-warm-50">
          <div className="h-7 w-7 border-4 border-warm-200 border-t-primary-500 rounded-full animate-spin" />
        </div>
      ) : error ? (
        <div className="flex-1 flex items-center justify-center p-8 bg-warm-50">
          <p className="text-danger-600 text-sm text-center">{error}</p>
        </div>
      ) : (
        <div className="bg-white flex-1 overflow-y-auto max-h-[520px] p-5">
          <div
            className="prose prose-sm max-w-none text-sm leading-relaxed"
            dangerouslySetInnerHTML={{ __html: html }}
          />
        </div>
      )}

      {/* Footer note + fallback flags */}
      {!loading && !error && (
        <div className="bg-warm-50 px-4 py-2.5 border-t border-warm-100 flex-shrink-0 space-y-1">
          <p className="text-xs text-warm-400">
            Shown with sample recipient values (e.g. name → “there”, your studio). Real recipients get their own details.
          </p>
          {fallbacks.length > 0 && (
            <p className="text-xs text-amber-700">
              <span className="font-semibold">Fallbacks used:</span>{' '}
              {fallbacks.map((f) => `${f.token} → ${f.rendered_as}`).join('   ')}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
